import { useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import CodeMirror from "@uiw/react-codemirror";
import { json } from "@codemirror/lang-json";
import { JsonView, allExpanded, defaultStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { NetworkTemplateService } from "@services/contentNetwork/networkTemplates/networkTemplateService";
import type { NetworkTemplate } from "@models/networkTemplate.model";

interface NetworkTemplateDetailProps {
  template: NetworkTemplate | null;
  onUpdated: (template: NetworkTemplate) => void;
  onDeleted: (templateId: number) => void;
}

export const NetworkTemplateDetail = ({ template, onUpdated, onDeleted }: NetworkTemplateDetailProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!template) {
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <p className="text-muted">Select a template to view its data.</p>
      </div>
    );
  }

  const startEditing = () => {
    setDraft(JSON.stringify(template.data ?? {}, null, 2));
    setError(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setError(null);
  };

  const handleSave = async () => {
    let parsed: Record<string, any>;
    try {
      parsed = JSON.parse(draft);
    } catch {
      setError("Invalid JSON — fix the syntax before saving.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await new NetworkTemplateService().update(template.id, { data: parsed });
      onUpdated(updated);
      setEditing(false);
    } catch (err) {
      console.error("Failed to update network template:", err);
      setError(err instanceof Error ? err.message : "Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await new NetworkTemplateService().delete(template.id);
      setConfirmingDelete(false);
      onDeleted(template.id);
    } catch (err) {
      console.error("Failed to delete network template:", err);
      // A template a launch still uses comes back as a 409 naming those launches. That
      // message is the whole answer, so it is shown as-is rather than summarised away.
      setError(err instanceof Error ? err.message : "Failed to delete template.");
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-3 h-100 d-flex flex-column" style={{ overflowY: "auto" }}>
      <div className="mb-3 d-flex justify-content-between align-items-start">
        <div>
          <h5 className="mb-1">{template.name}</h5>
          <div className="text-muted">
            <small>
              <strong>Network:</strong> {template.network} &nbsp;·&nbsp;
              <strong>ID:</strong> {template.id}
            </small>
          </div>
        </div>

        {editing ? (
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={cancelEditing} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" /> : "Save"}
            </Button>
          </div>
        ) : (
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-primary" onClick={startEditing} className="d-flex align-items-center gap-1">
              <i className="bi bi-pencil"></i>
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              onClick={() => setConfirmingDelete(true)}
              className="d-flex align-items-center gap-1"
            >
              <i className="bi bi-trash"></i>
              Delete
            </Button>
          </div>
        )}
      </div>

      {confirmingDelete && (
        <div className="alert alert-warning d-flex justify-content-between align-items-center py-2 px-3">
          <span>
            Delete <strong>{template.name}</strong>? Launches using it keep publishing from it
            until they are pointed elsewhere, so this is refused while any still do.
          </span>
          <span className="d-flex gap-2 ms-3">
            <Button size="sm" variant="outline-secondary" onClick={() => setConfirmingDelete(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button size="sm" variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Spinner size="sm" animation="border" /> : "Delete"}
            </Button>
          </span>
        </div>
      )}

      {error && <div className="alert alert-danger py-2 px-3">{error}</div>}

      {editing ? (
        <div className="border rounded">
          <CodeMirror
            value={draft}
            onChange={setDraft}
            extensions={[json()]}
            basicSetup={{ lineNumbers: true, foldGutter: true, bracketMatching: true, closeBrackets: true }}
            minHeight="300px"
          />
        </div>
      ) : (
        <div className="border rounded p-2 bg-white">
          <JsonView data={template.data ?? {}} style={defaultStyles} shouldExpandNode={allExpanded} />
        </div>
      )}
    </div>
  );
};
